import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, Loader2, CheckCircle2, ChevronLeft } from "lucide-react";
import api from "@/lib/api";

// Aug 2026 — technician Quick Test Booking. Creates a 100% STANDARD
// water-test booking (same schema as /book) with prefill, a 2-option
// sample method, an OFF-by-default 20% discount and pay-now / SMS link.
export default function QuickTestBooking() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [tests, setTests] = useState([]);
  const [cust, setCust] = useState({ name: "", email: "", mobile: "", address: "" });
  const [testId, setTestId] = useState("");
  const [sampleMethod, setSampleMethod] = useState("tech_collected"); // default per spec
  const [discount, setDiscount] = useState(false);                    // OFF by default per spec
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);
  const [ctx, setCtx] = useState({ appointment_id: null, inspection_id: null });

  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      const ts = data.tests || [];
      setTests(ts);
      if (ts.length) setTestId(ts[0].id);
    }).catch(() => toast.error("Could not load test catalogue"));
    const aid = params.get("appointment_id");
    if (aid) {
      api.get(`/field/quick-booking/prefill?appointment_id=${aid}`).then(({ data }) => {
        setCust((c) => ({ ...c, ...Object.fromEntries(Object.entries(data.customer || {}).filter(([, v]) => v)) }));
        setCtx({ appointment_id: data.appointment_id, inspection_id: data.inspection_id });
      }).catch(() => {});
    }
  }, []); // eslint-disable-line

  const test = useMemo(() => tests.find((t) => t.id === testId), [tests, testId]);
  const price = Number(test?.retail || 0);
  const discounted = Math.round(price * 0.8 * 100) / 100;

  const submit = async (paymentChoice) => {
    if (!cust.name.trim() || !cust.email.trim()) { toast.error("Customer name and email are required"); return; }
    if (paymentChoice === "sms_link" && !cust.mobile.trim()) { toast.error("Mobile number needed for the SMS payment link"); return; }
    setBusy(paymentChoice);
    try {
      const { data } = await api.post("/field/quick-booking", {
        test_id: testId, test_name: test?.name || testId,
        customer: cust, sample_method: sampleMethod,
        apply_discount: discount, payment_choice: paymentChoice,
        appointment_id: ctx.appointment_id, inspection_id: ctx.inspection_id,
      });
      if (data.payment_link_error) {
        toast.error(`Booking ${data.reference} saved, but the payment link failed: ${data.payment_link_error}`);
        setDone({ ...data, mode: "error" });
        return;
      }
      if (paymentChoice === "pay_now") {
        window.location.assign(data.payment_url);
        return;
      }
      setDone({ ...data, mode: "sms" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setBusy(""); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12 max-w-md mx-auto text-center" data-testid="quick-booking-done">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h1 className="mt-3 text-lg font-bold text-slate-900">Test Booking Complete</h1>
        <p className="mt-2 text-sm text-slate-600" data-testid="quick-booking-done-msg">
          Payment link sent to {done.customer_first_name}.<br />
          Payment status: <b>Awaiting Payment</b>
        </p>
        {done.sample_method === "tech_collected" && (
          <p className="mt-3 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="quick-booking-hold-note">
            Sample collected — testing will remain on hold until payment is received.
          </p>
        )}
        <p className="mt-3 text-[11px] text-slate-400 font-mono">{done.reference}</p>
        <button data-testid="quick-booking-done-btn" onClick={() => nav("/field/today")}
          className="mt-6 w-full rounded-full bg-slate-900 text-white font-bold py-3">Done</button>
      </div>
    );
  }

  const Opt = ({ val, label, tid }) => (
    <button type="button" data-testid={tid} onClick={() => setSampleMethod(val)}
      className={`px-3 py-2 rounded-full text-[12px] font-semibold border-2 mr-2 mb-2 ${sampleMethod === val ? "bg-cyan-600 border-cyan-600 text-white" : "border-slate-200 text-slate-700 bg-white"}`}>
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 max-w-md mx-auto" data-testid="quick-booking-page">
      <button onClick={() => nav("/field/today")} className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1"><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
      <div className="mt-2 flex items-center gap-2">
        <FlaskConical className="w-6 h-6 text-cyan-600" />
        <h1 className="text-lg font-bold text-slate-900">Quick Test Booking</h1>
      </div>
      <p className="mt-1 text-xs text-slate-500">Creates a standard AWARE water-test booking — same tests, pricing and lab workflow as the customer booking page.</p>

      <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-4">
        <h2 className="text-sm font-bold text-slate-900">Customer details</h2>
        {["name", "email", "mobile", "address"].map((k) => (
          <input key={k} data-testid={`qb-${k}`} value={cust[k]}
            onChange={(e) => setCust({ ...cust, [k]: e.target.value })}
            placeholder={k === "name" ? "Full name *" : k === "email" ? "Email *" : k === "mobile" ? "Mobile" : "Property address"}
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
        ))}
      </div>

      <div className="mt-3 bg-white rounded-2xl border border-slate-200 p-4">
        <h2 className="text-sm font-bold text-slate-900">Test selection</h2>
        <select data-testid="qb-test" value={testId} onChange={(e) => setTestId(e.target.value)}
          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
          {tests.map((t) => <option key={t.id} value={t.id}>{t.name} — ${Number(t.retail).toFixed(0)}</option>)}
        </select>
      </div>

      <div className="mt-3 bg-white rounded-2xl border border-slate-200 p-4">
        <h2 className="text-sm font-bold text-slate-900">How will the sample be provided?</h2>
        <div className="mt-2">
          <Opt val="tech_collected" label="Sample collected by Tech" tid="qb-sample-tech" />
          <Opt val="customer_dropoff" label="Customer delivering a sample to the lab" tid="qb-sample-dropoff" />
        </div>
      </div>

      <div className="mt-3 bg-white rounded-2xl border border-slate-200 p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="qb-discount" checked={discount} onChange={(e) => setDiscount(e.target.checked)}
            className="w-5 h-5 accent-cyan-600" />
          <span className="text-sm font-bold text-slate-900">Apply 20% discount</span>
        </label>
        <div className="mt-2 text-sm" data-testid="qb-price">
          {discount ? (
            <span><s className="text-slate-400">${price.toFixed(2)}</s>{" "}
              <b className="text-emerald-700">${discounted.toFixed(2)}</b>
              <span className="ml-1 text-[11px] text-emerald-700 font-semibold">20% applied</span></span>
          ) : (
            <b className="text-slate-900">${price.toFixed(2)}</b>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-2">
        <button data-testid="qb-pay-now" disabled={!!busy} onClick={() => submit("pay_now")}
          className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 disabled:opacity-60">
          {busy === "pay_now" ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
          Pay Now (Card)
        </button>
        <button data-testid="qb-send-sms" disabled={!!busy} onClick={() => submit("sms_link")}
          className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 disabled:opacity-60">
          {busy === "sms_link" ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
          Send Payment Link by SMS
        </button>
      </div>
    </div>
  );
}
